import { inject, Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable, from } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { CognitoUtilsService } from "../common/auth/src/services/cognito-utils.service";

/**
 * Service to intercept HTTP requests and attach authentication headers
 * Uses the same pattern as Kosmo - gets token from Cognito session, not localStorage
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  /** Cognito utilities service */
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);

  /**
   * Intercepts the HTTP request and attaches authentication headers if a token is available
   * Gets token from Cognito session (same as Kosmo pattern)
   * @param request the original HTTP request
   * @param next the next HTTP handler
   * @returns an Observable of the HTTP event
   */
  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const requestStartTime = performance.now();
    
    // Skip auth for S3 uploads (presigned URLs)
    if (request.headers.has('X-Skip-Interceptor')) {
      return next.handle(request);
    }

    // Use the same pattern as Kosmo - get token from session
    return from(this._cognitoUtilsService.getSession()).pipe(
      map(session => {
        const token = session?.getAccessToken().getJwtToken();
        return token;
      }),
      switchMap(token => {
        
        // If no token, proceed without authentication
        if (!token) {
          return next.handle(request).pipe(
            catchError(error => {
              throw error;
            })
          );
        }
        
        // Clone request with authorization header
        const authRequest = request.clone({
          headers: request.headers.set(
            "Authorization",
            `Bearer ${token}`,
          ),
        });
        
        return next.handle(authRequest).pipe(
          catchError(error => {
            throw error;
          })
        );
      }),
      catchError((sessionError) => {
        // On any error, proceed without authentication
        return next.handle(request).pipe(
          catchError(error => {
            throw error;
          })
        );
      })
    );
  }
}